// PlanReviewCard — shown after a plan_proposed SSE event for ANALYSIS/DEEP_WORK
// queries. The user can drop steps, reorder them, rephrase sub-tasks, and
// change the answer mode before approving.

import { useCallback, useState } from "react";

import { API_BASE, readCachedAccessToken } from "@/services/api/client";
import type { ChatStreamEvent } from "@/services/chat/streamService";
import type { PlanStep } from "@/types/eryndal";
import { cn } from "@/utils/cn";

interface PlanEditOp {
  op: "drop" | "reorder" | "rephrase" | "change_answer_mode";
  step_index?: number;
  new_indices?: number[];
  new_text?: string;
  new_mode?: string;
}

export interface PlanReviewCardProps {
  plan: PlanStep[];
  rationale?: string;
  initialAnswerMode?: string;
  sessionId: string;
  getAccessToken?: () => Promise<string | null>;
  onApprove: (stream: AsyncGenerator<ChatStreamEvent>) => void;
  onCancel: () => void;
}

const ANSWER_MODES = ["narrative", "table", "bundle"] as const;

async function* approvePlanStream(
  sessionId: string,
  edits: PlanEditOp[],
  getAccessToken?: () => Promise<string | null>,
): AsyncGenerator<ChatStreamEvent> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch {
      token = null;
    }
  }
  if (!token) token = readCachedAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/chat/sessions/${sessionId}/plan/approve`, {
    method: "POST",
    headers,
    body: JSON.stringify({ edits }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Plan approve failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseBlock(raw);
        if (parsed) yield parsed;
        boundary = buffer.indexOf("\n\n");
      }
    }
    if (buffer.trim().length) {
      const parsed = parseBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseBlock(raw: string): ChatStreamEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
  }
  if (!event || event === "message") return null;
  let payload: unknown = dataLines.join("\n");
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    // pass through
  }
  return { kind: event as ChatStreamEvent["kind"], data: payload } as ChatStreamEvent;
}

const SUB_AGENT_LABELS: Record<string, string> = {
  regulatory: "Regulatory",
  vulnerability: "Vulnerability",
  legal_review: "Legal Review",
  vendor_risk: "Vendor Risk",
  framework_crosswalk: "Framework Crosswalk",
  incident_response: "Incident Response",
  trust_eidas: "Trust & eIDAS",
  aml_fraud: "AML & Fraud",
  pen_test: "Pen Test",
  detection_engineering: "Detection Engineering",
  cloud_security: "Cloud Security",
  ai_security: "AI Security",
  identity_crypto: "Identity & Crypto",
  data_governance: "Data Governance",
};

export function PlanReviewCard({
  plan,
  rationale,
  initialAnswerMode = "narrative",
  sessionId,
  getAccessToken,
  onApprove,
  onCancel,
}: PlanReviewCardProps) {
  const [steps, setSteps] = useState<PlanStep[]>(plan);
  const [answerMode, setAnswerMode] = useState(initialAnswerMode);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Track accumulated edits to send to the backend.
  const [edits, setEdits] = useState<PlanEditOp[]>([]);

  const addEdit = useCallback((op: PlanEditOp) => {
    setEdits((prev) => [...prev, op]);
  }, []);

  const handleDrop = useCallback(
    (index: number) => {
      addEdit({ op: "drop", step_index: index });
      setSteps((prev) => prev.filter((_, i) => i !== index));
    },
    [addEdit],
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0) return;
      const newOrder = steps.map((_, i) => i);
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      addEdit({ op: "reorder", new_indices: newOrder });
      setSteps((prev) => {
        const next = [...prev];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        return next;
      });
    },
    [addEdit, steps],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index === steps.length - 1) return;
      const newOrder = steps.map((_, i) => i);
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      addEdit({ op: "reorder", new_indices: newOrder });
      setSteps((prev) => {
        const next = [...prev];
        [next[index], next[index + 1]] = [next[index + 1], next[index]];
        return next;
      });
    },
    [addEdit, steps],
  );

  const handleRephrase = useCallback(
    (index: number) => {
      if (!editText.trim()) {
        setEditingIndex(null);
        return;
      }
      addEdit({ op: "rephrase", step_index: index, new_text: editText.trim() });
      setSteps((prev) =>
        prev.map((s, i) => (i === index ? { ...s, sub_task: editText.trim() } : s)),
      );
      setEditingIndex(null);
      setEditText("");
    },
    [addEdit, editText],
  );

  const handleAnswerModeChange = useCallback(
    (mode: string) => {
      addEdit({ op: "change_answer_mode", new_mode: mode });
      setAnswerMode(mode);
    },
    [addEdit],
  );

  const handleApprove = useCallback(async () => {
    setSubmitting(true);
    try {
      const stream = approvePlanStream(sessionId, edits, getAccessToken);
      onApprove(stream);
    } catch {
      setSubmitting(false);
    }
  }, [edits, getAccessToken, onApprove, sessionId]);

  return (
    <div className="animate-ery-rise space-y-4 rounded-card border border-oxblood-300 bg-oxblood-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-mono">Plan ready for review</p>
          {rationale && (
            <p className="mt-1.5 font-serif text-xs leading-relaxed text-warmgray-500">
              {rationale}
            </p>
          )}
        </div>
        <button
          onClick={onCancel}
          className="shrink-0 font-mono text-xs text-oxblood-400 underline underline-offset-2 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
          disabled={submitting}
        >
          Cancel
        </button>
      </div>

      <ol className="space-y-2">
        {steps.map((step, i) => (
          <li
            key={`${step.sub_agent}-${i}`}
            className="flex gap-2 rounded-panel border border-oxblood-200 bg-parchment-50 p-2"
          >
            <span className="w-5 shrink-0 text-right font-mono text-oxblood-400">{i + 1}.</span>
            <div className="min-w-0 flex-1">
              <p className="font-serif font-medium text-espresso-700">
                {SUB_AGENT_LABELS[step.sub_agent] ?? step.sub_agent}
              </p>
              {editingIndex === i ? (
                <div className="mt-1 flex gap-2">
                  <textarea
                    className="flex-1 resize-none rounded-chip border border-oxblood-200 bg-parchment-50 p-1 font-serif text-xs text-espresso-700 focus:border-oxblood-400 focus:outline-none focus:ring-2 focus:ring-oxblood-200"
                    rows={2}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoFocus
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleRephrase(i)}
                      className="btn-oxblood h-[26px] px-2 text-[0.7rem]"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingIndex(null);
                        setEditText("");
                      }}
                      className="btn-outline h-[26px] px-2 text-[0.7rem]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-0.5 font-serif text-xs leading-relaxed text-warmgray-500">
                  {step.sub_task}
                </p>
              )}
            </div>
            {editingIndex !== i && (
              <div className="flex shrink-0 flex-col gap-0.5">
                <button
                  title="Move up"
                  onClick={() => handleMoveUp(i)}
                  disabled={i === 0}
                  className={cn(
                    "px-1 text-oxblood-400 transition-colors duration-200 ease-atelier",
                    "hover:text-oxblood-600 disabled:opacity-30",
                    "text-xs leading-none",
                  )}
                >
                  ↑
                </button>
                <button
                  title="Move down"
                  onClick={() => handleMoveDown(i)}
                  disabled={i === steps.length - 1}
                  className={cn(
                    "px-1 text-oxblood-400 transition-colors duration-200 ease-atelier",
                    "hover:text-oxblood-600 disabled:opacity-30",
                    "text-xs leading-none",
                  )}
                >
                  ↓
                </button>
                <button
                  title="Edit task"
                  onClick={() => {
                    setEditingIndex(i);
                    setEditText(step.sub_task);
                  }}
                  className="px-1 text-xs leading-none text-oxblood-400 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
                >
                  ✎
                </button>
                <button
                  title="Remove step"
                  onClick={() => handleDrop(i)}
                  className="px-1 text-xs leading-none text-warmgray-300 transition-colors duration-200 ease-atelier hover:text-oxblood-500"
                >
                  ✕
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="flex items-center gap-3">
        <span className="label-mono-muted">Answer format</span>
        {ANSWER_MODES.map((mode) => (
          <label key={mode} className="flex cursor-pointer items-center gap-1 text-xs">
            <input
              type="radio"
              name="answer_mode"
              value={mode}
              checked={answerMode === mode}
              onChange={() => handleAnswerModeChange(mode)}
              className="accent-oxblood-500"
            />
            <span className="font-serif capitalize text-espresso-700">{mode}</span>
          </label>
        ))}
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleApprove}
          disabled={submitting || steps.length === 0}
          className="btn-oxblood h-[34px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          {submitting ? "Starting…" : "Approve & run"}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="btn-outline h-[34px] disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
